import { useCopy } from '@/hooks/shared/useCopy';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import formatScheduledLessonCopyMessage from '@/utils/scheduled-lesson-report/formatScheduledLessonCopyMessage';
import { useQuery } from '@tanstack/react-query';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import {
  homeworkActivitiesQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';

export type UseCopyScheduledLessonReportParams = {
  scheduledLesson: ScheduledLesson;
  klass: Klass;
};

export default function useCopyScheduledLessonReport({
  scheduledLesson,
  klass,
}: UseCopyScheduledLessonReportParams) {
  const { handleCopy, isCopied } = useCopy();
  const teacherId = scheduledLesson.teacher || klass.teacherId;

  const { data: teacher, isInitialLoading: isTeacherLoading } = useQuery({
    ...usersQueryKeys.get(teacherId ?? 0),
    enabled: !!teacherId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const {
    results: homeworkActivities,
    isInitialLoading: isHomeworkActivitiesLoading,
  } = useListService({
    ...homeworkActivitiesQueryKeys.list({
      activity: scheduledLesson.homeworkActivity ?? 0,
    }),
    enabled:
      scheduledLesson.hasHomeworkActivity && !!scheduledLesson.homeworkActivity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const copyReport = () => {
    handleCopy(
      formatScheduledLessonCopyMessage({
        scheduledLesson,
        klass,
        teacher,
        homeworkActivities,
      }),
    );
  };

  return {
    copyReport,
    isLoading: isTeacherLoading || isHomeworkActivitiesLoading,
    isCopied,
  };
}
